import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useDatabases } from '../hooks/useDatabases';
import { useAnnouncements } from '../hooks/useAnnouncements';

export function InformativoTab() {
  const { databases, list: listDatabases } = useDatabases();
  const { create, loading } = useAnnouncements();
  const [selectedDatabaseIds, setSelectedDatabaseIds] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    text: '',
    priority: 3,
    status: true,
  });
  const [mediaItems, setMediaItems] = useState([]);

  useEffect(() => {
    listDatabases();
  }, [listDatabases]);


  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleDatabase = (databaseId) => {
    setSelectedDatabaseIds((prev) =>
      prev.includes(databaseId)
        ? prev.filter((id) => id !== databaseId)
        : [...prev, databaseId]
    );
  };

  const handleToggleAllDatabases = () => {
    const activeDatabases = databases.filter((db) => db.isActive !== false).map((db) => db.id);
    if (selectedDatabaseIds.length === activeDatabases.length) {
      setSelectedDatabaseIds([]);
    } else {
      setSelectedDatabaseIds(activeDatabases);
    }
  };

  const handleAddMedia = () => {
    setMediaItems((prev) => [...prev, { file: null, caption: '' }]);
  };

  const handleRemoveMedia = (index) => {
    setMediaItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleMediaChange = (index, field, value) => {
    setMediaItems((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item))
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }
    if (!formData.text.trim() && mediaItems.length === 0) {
      toast.error('Texto ou mídia são obrigatórios');
      return;
    }
    if (selectedDatabaseIds.length === 0) {
      toast.error('Selecione pelo menos uma instância');
      return;
    }

    const formPayload = new FormData();
    formPayload.append('title', formData.title.trim());
    formPayload.append('text', formData.text.trim());
    formPayload.append('priority', String(Number(formData.priority) || 3));
    formPayload.append('status', String(formData.status));
    formPayload.append('companyId', '1');
    selectedDatabaseIds.forEach((id) => formPayload.append('targetDatabaseIds', id));

    mediaItems.forEach((item) => {
      if (item.file) {
        formPayload.append('files', item.file);
        formPayload.append('captions', item.caption || '');
      }
    });

    try {
      await create(formPayload);
      toast.success('Informativo criado em instâncias selecionadas');
      setFormData({ title: '', text: '', priority: 3, status: true });
      setMediaItems([]);
      setSelectedDatabaseIds([]);
    } catch (err) {
      toast.error(err.message || 'Falha ao criar informativo');
    }
  };

  const activeDatabases = databases.filter((db) => db.isActive !== false);
  const allSelected = activeDatabases.length > 0 && selectedDatabaseIds.length === activeDatabases.length;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-semibold text-gray-900">Informativo</h2>
        <p className="mt-2 text-gray-600">
          Crie um informativo e envie para as instâncias selecionadas.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Título</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Título do informativo"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Prioridade</label>
            <select
              value={formData.priority}
              onChange={(e) => handleChange('priority', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value={1}>Alta</option>
              <option value={2}>Média</option>
              <option value={3}>Baixa</option>
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Texto</label>
            <textarea
              rows={5}
              value={formData.text}
              onChange={(e) => handleChange('text', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Conteúdo do informativo"
            />
          </div>

          <div className="lg:col-span-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">Instâncias alvo</label>
              <button
                type="button"
                onClick={handleToggleAllDatabases}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {allSelected ? 'Desmarcar todos' : 'Selecionar todos'}
              </button>
            </div>

            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {activeDatabases.length === 0 ? (
                <div className="rounded-md border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-800">
                  Nenhuma instância ativa encontrada.
                </div>
              ) : (
                activeDatabases.map((db) => (
                  <label
                    key={db.id}
                    className="flex items-center gap-3 rounded-md border border-gray-200 px-3 py-2"
                  >
                    <input
                      type="checkbox"
                      checked={selectedDatabaseIds.includes(db.id)}
                      onChange={() => handleToggleDatabase(db.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{db.displayName || db.dbName}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Mídias</p>
              <p className="text-sm text-gray-500">Adicione arquivos do computador e legendas para cada item.</p>
            </div>
            <button
              type="button"
              onClick={handleAddMedia}
              className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Adicionar mídia
            </button>
          </div>

          {mediaItems.length === 0 && (
            <div className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-500">
              Nenhuma mídia adicionada.
            </div>
          )}

          {mediaItems.map((item, index) => (
            <div key={index} className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm font-semibold text-gray-800">Mídia {index + 1}</p>
                <button
                  type="button"
                  onClick={() => handleRemoveMedia(index)}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Remover
                </button>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Arquivo</label>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => handleMediaChange(index, 'file', e.target.files?.[0] || null)}
                    className="mt-1 block w-full text-sm text-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Legenda</label>
                  <textarea
                    rows={3}
                    value={item.caption}
                    onChange={(e) => handleMediaChange(index, 'caption', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Legenda da mídia"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center rounded-md bg-green-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Salvando...' : 'Criar Informativo'}
          </button>
        </div>
      </form>
    </div>
  );
}
